import React, { useState } from "react";
import { Button, Table, Progress, Alert, Card } from "reactstrap";
import * as XLSX from "xlsx";
import { useTenant } from "../../../hooks/useTenant";
import { ContentLoader, Breadcrumb } from "../../../components/Common";
import { toast } from "react-toastify";
import MemberService from "../../../services/member_service";
import { useMembers } from "../../../hooks/useMembers";

const ImportMembersPage = () => {
    document.title = "Importar Alunos | NeoApp - Sistema de Gestão";

    const { idTenant, user } = useTenant();
    const { refresh: refreshMembers } = useMembers();
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [stats, setStats] = useState(null);

    const downloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([
            {
                Nome: "Exemplo",
                Sobrenome: "Aluno da Silva",
                "E-mail": "aluno@exemplo.com.br",
                "N de Telefone": "(11) 99999-9999",
                CPF: "123.456.789-00",
                "Data de Nascimento": "1990-01-01",
                "Gênero": "M",
                "Estado Civil": "solteiro",
                CEP: "00000-000",
                "Endereço": "Rua Exemplo",
                "Número": "123",
                Complemento: "Apt 1",
                Bairro: "Centro",
                Cidade: "São Paulo",
                Estado: "SP",
            }
        ]);
        // Ajusta largura visual das colunas do modelo
        ws['!cols'] = [
            { wpx: 120 }, { wpx: 180 }, { wpx: 200 }, { wpx: 120 }, { wpx: 120 },
            { wpx: 130 }, { wpx: 80 }, { wpx: 100 }, { wpx: 100 }, { wpx: 200 },
            { wpx: 60 }, { wpx: 100 }, { wpx: 120 }, { wpx: 120 }, { wpx: 60 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Modelo");
        XLSX.writeFile(wb, "Modelo_Importacao_Alunos.xlsx");
    };

    // Validação de formato ou CPF etc poderia ser feita durante a montagem
    // Basic rules to map standard Excel header names to Member fields
    const mapColumn = (row, possibleNames) => {
        const key = Object.keys(row).find(k =>
            possibleNames.some(name => k.toLowerCase().trim().includes(name.toLowerCase()))
        );
        return key ? (row[key] || "").toString().trim() : "";
    };

    const handleFileUpload = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        setFile(selectedFile);
        setProgress(0);
        setStats(null);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary", cellDates: true });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                // raw: false faz com que datas do excel venham como string formatada se tiverem cell format,
                // mas combinando com cellDates: true, caso a data entre como Date no JS a gente sanitiza
                const data = XLSX.utils.sheet_to_json(ws, { raw: false, dateNF: "yyyy-mm-dd" });

                if (data.length === 0) {
                    toast.error("A planilha parece estar vazia.");
                    return;
                }

                // Formatter para nomes (Maiúsculas/minúsculas)
                const formatName = (name) => {
                    if (!name) return "";
                    return name.trim().split(/\s+/).map((word, index) => {
                        const lower = word.toLowerCase();
                        if (index > 0 && ['da', 'de', 'do', 'das', 'dos', 'e'].includes(lower)) {
                            return lower;
                        }
                        return lower.charAt(0).toUpperCase() + lower.slice(1);
                    }).join(' ');
                };
                
                const parseDateStr = (dateStr) => {
                    if (!dateStr) return null;
                    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
                    if (match) return match[0];
                    const pts = dateStr.split("/");
                    if (pts.length === 3) {
                        return `${pts[2]}-${pts[1].padStart(2, '0')}-${pts[0].padStart(2, '0')}`;
                    }
                    return dateStr;
                };

                // Normalizing data to standard Member properties
                const normalized = data.map((row) => {
                    const rawFirstName = mapColumn(row, ["nome", "first name", "aluno"]);
                    const rawLastName = mapColumn(row, ["sobrenome", "last name"]);

                    let firstName = "";
                    let lastName = "";

                    if (rawLastName) {
                        firstName = rawFirstName;
                        lastName = rawLastName;
                    } else if (rawFirstName) {
                        const parts = rawFirstName.split(" ");
                        firstName = parts[0] || "";
                        lastName = parts.slice(1).join(" ") || "";
                    }

                    firstName = formatName(firstName);
                    lastName = formatName(lastName);
                    const fullName = `${firstName} ${lastName}`.trim();

                    const emailRaw = mapColumn(row, ["email", "e-mail"]);
                    const email = emailRaw ? emailRaw.toLowerCase().trim() : "";
                    
                    const genderMap = {"m": "M", "masculino": "M", "f": "F", "feminino": "F", "o": "O", "outro": "O"};
                    const rawGen = mapColumn(row, ["gênero", "genero", "sexo", "gender"]).toLowerCase();

                    return {
                        firstName,
                        lastName,
                        fullName,
                        email,
                        phone: mapColumn(row, ["telefone", "celular", "whatsapp", "phone", "n de telefone"]),
                        document: mapColumn(row, ["cpf", "document", "documento"]),
                        birthDate: parseDateStr(mapColumn(row, ["nascimento", "birthdate", "data de nascimento", "data nascimento"])),
                        gender: genderMap[rawGen] || "",
                        maritalStatus: mapColumn(row, ["estado civil", "estado_civil", "marital status"]),
                        zipCode: mapColumn(row, ["cep", "código postal"]),
                        address: mapColumn(row, ["endereço", "endereco", "rua", "logradouro", "address", "street"]),
                        number: mapColumn(row, ["número", "numero", "number"]),
                        complement: mapColumn(row, ["complemento", "complement"]),
                        neighborhood: mapColumn(row, ["bairro", "neighborhood"]),
                        city: mapColumn(row, ["cidade", "city"]),
                        state: mapColumn(row, ["estado", "uf", "state"]),
                        country: mapColumn(row, ["país", "pais", "country"]) || "Brasil",
                        status: "lead", // Padrão de entrada
                        raw: row // Pra debug se necessário
                    };
                }).filter(m => m.firstName); // ignora linhas em branco sem nome

                setPreviewData(normalized);
                toast.success(`${normalized.length} registros identificados!`);
            } catch (err) {
                console.error(err);
                toast.error("Erro ao ler o arquivo. Certifique-se de que é um Excel ou CSV válido.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const handleImport = async () => {
        if (!idTenant || previewData.length === 0) return;

        setIsImporting(true);
        let successCount = 0;
        let errorCount = 0;

        try {
            // Em vez de salvar de 1 em 1 por fora e arriscar colisão de idMember (transações encavaladas),
            // Vamos iterar um a um com o serviço padrão que já tem 'runTransaction'.
            for (let i = 0; i < previewData.length; i++) {
                const member = previewData[i];
                try {
                    await MemberService.save(member, idTenant, user, []);
                    successCount++;
                } catch (error) {
                    console.error("Erro ao importar registro", member, error);
                    errorCount++;
                }

                setProgress(Math.round(((i + 1) / previewData.length) * 100));
            }

            // Recarrega o cache centralizado de membros para refletir os novos cadastros
            await refreshMembers();

            toast.success("Importação concluída com sucesso!");
            setFile(null);
            setPreviewData([]);
        } catch (globalError) {
            console.error("Erro generalizado na importação:", globalError);
            toast.error("Processo interrompido por um erro Crítico.");
        } finally {
            setStats({ success: successCount, errors: errorCount, total: previewData.length });
            setIsImporting(false);
            setProgress(100);
        }
    };

    const cancelImport = () => {
        setFile(null);
        setPreviewData([]);
        setProgress(0);
        setStats(null);
    };

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Importar Alunos" />
            </div>

            <Card 
                className="overflow-hidden bg-white shadow-sm p-4"
                style={{ 
                    borderRadius: "16px",
                    border: "2px solid #482712",
                    boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                    fontFamily: "Outfit, sans-serif"
                }}
            >
                {stats && (
                    <Alert 
                        color={stats.errors > 0 ? "warning" : "success"} 
                        className="mb-4 border-0 d-flex align-items-center gap-2" 
                        style={{ 
                            borderRadius: "10px", 
                            fontFamily: "Outfit, sans-serif",
                            backgroundColor: stats.errors > 0 ? "rgba(226, 102, 28, 0.08)" : "rgba(46, 125, 50, 0.08)",
                            color: stats.errors > 0 ? "#e2661c" : "#2e7d32",
                            border: stats.errors > 0 ? "1px solid rgba(226, 102, 28, 0.15)" : "1px solid rgba(46, 125, 50, 0.15)"
                        }}
                        fade={false}
                    >
                        <i className={`mdi ${stats.errors > 0 ? 'mdi-alert-circle-outline' : 'mdi-checkbox-marked-circle-outline'} font-size-18`}></i>
                        <div>
                            Importação finalizada! <strong>{stats.success}</strong> adicionados com sucesso.
                            {stats.errors > 0 && <span> ({stats.errors} falhas)</span>}
                        </div>
                    </Alert>
                )}

                {!file && (
                    <div 
                        className="text-center py-5 border" 
                        style={{ 
                            borderStyle: 'dashed', 
                            borderWidth: '2px', 
                            borderColor: 'rgba(72, 39, 18, 0.20)', 
                            borderRadius: '12px',
                            backgroundColor: 'rgba(226, 102, 28, 0.02)',
                            padding: '40px 20px'
                        }}
                    >
                        <div 
                            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                            style={{
                                width: "64px",
                                height: "64px",
                                background: "rgba(226, 102, 28, 0.08)",
                                border: "1px solid rgba(226, 102, 28, 0.20)"
                            }}
                        >
                            <i className="mdi mdi-cloud-upload-outline font-size-32" style={{ color: "#e2661c" }}></i>
                        </div>
                        <h5 className="fw-bold text-dark mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Arraste a planilha ou clique para buscar</h5>
                        <p className="text-muted mb-4 font-size-13" style={{ maxWidth: '450px', margin: '0 auto 24px' }}>
                            Suporta arquivos .xlsx e .csv. Limite recomendado de 500 registros por vez para melhor processamento.
                        </p>
                        <div>
                            <input
                                type="file"
                                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                id="upload-excel"
                                className="d-none"
                                onChange={handleFileUpload}
                            />
                            <div className="d-flex justify-content-center gap-3">
                                <label 
                                    htmlFor="upload-excel" 
                                    className="btn btn-primary px-4 mb-0 fw-bold d-inline-flex align-items-center justify-content-center"
                                    style={{ borderRadius: "10px", height: "42px", fontFamily: "Outfit, sans-serif", cursor: 'pointer' }}
                                >
                                    Selecionar Arquivo
                                </label>
                                <Button 
                                    color="light" 
                                    className="px-4 fw-bold border bg-white d-inline-flex align-items-center justify-content-center" 
                                    style={{ borderRadius: "10px", height: "42px", color: "#e2661c", borderColor: "rgba(226, 102, 28, 0.30)", fontFamily: "Outfit, sans-serif" }}
                                    onClick={downloadTemplate}
                                >
                                    <i className="mdi mdi-download-outline me-2"></i> Baixar Modelo
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {previewData.length > 0 && !isImporting && progress === 0 && (
                    <div>
                        <div 
                            className="d-flex align-items-center justify-content-between mb-4 pb-3 mt-2"
                            style={{ borderBottom: "1.5px solid rgba(72, 39, 18, 0.12)" }}
                        >
                            <div>
                                <h5 className="fw-bold mb-1 text-dark" style={{ fontFamily: "Outfit, sans-serif" }}>Pré-visualização dos Dados</h5>
                                <span className="text-muted font-size-13">{previewData.length} registros prontos para entrar no sistema.</span>
                            </div>
                            <div className="d-flex gap-2">
                                <Button 
                                    color="light" 
                                    className="btn btn-outline-light border text-dark fw-bold px-4" 
                                    style={{ borderRadius: "10px", height: "42px", fontFamily: "Outfit, sans-serif", borderColor: "rgba(72, 39, 18, 0.15)", backgroundColor: "#fff" }}
                                    onClick={cancelImport}
                                >
                                    Cancelar
                                </Button>
                                <Button 
                                    color="success" 
                                    className="px-4 d-flex align-items-center justify-content-center fw-bold" 
                                    style={{ borderRadius: "10px", height: "42px", fontFamily: "Outfit, sans-serif", backgroundColor: "#2e7d32", borderColor: "#2e7d32" }}
                                    onClick={handleImport}
                                >
                                    <i className="mdi mdi-database-import-outline me-2"></i> Iniciar Importação
                                </Button>
                            </div>
                        </div>

                        <div className="table-responsive" style={{ maxHeight: "400px", borderRadius: "10px", border: "1.5px solid rgba(72, 39, 18, 0.12)" }}>
                            <Table className="table mb-0 table-hover align-middle">
                                <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th className="font-size-11 fw-bold text-muted py-2.5 ps-3 text-uppercase">#</th>
                                        <th className="font-size-11 fw-bold text-muted py-2.5 text-uppercase" style={{ minWidth: "150px" }}>Nome do Aluno</th>
                                        <th className="font-size-11 fw-bold text-muted py-2.5 text-uppercase">E-mail</th>
                                        <th className="font-size-11 fw-bold text-muted py-2.5 text-uppercase">Telefone</th>
                                        <th className="font-size-11 fw-bold text-muted py-2.5 text-uppercase">CPF</th>
                                        <th className="font-size-11 fw-bold text-muted py-2.5 text-uppercase">Nascimento</th>
                                        <th className="font-size-11 fw-bold text-muted py-2.5 text-uppercase">Sexo</th>
                                        <th className="font-size-11 fw-bold text-muted py-2.5 pe-3 text-uppercase">Endereço</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.slice(0, 100).map((member, index) => (
                                        <tr key={index}>
                                            <td className="text-muted ps-3" style={{ width: "40px" }}>{index + 1}</td>
                                            <td className="fw-semibold text-dark">{member.fullName}</td>
                                            <td>{member.email || <span className="text-warning small fst-italic">Sem e-mail</span>}</td>
                                            <td>{member.phone || <span className="text-warning small fst-italic">Sem telefone</span>}</td>
                                            <td>{member.document || <span className="text-muted small fst-italic">-</span>}</td>
                                            <td>{member.birthDate ? new Date(member.birthDate).toLocaleDateString('pt-BR', {timeZone: 'UTC'}) : <span className="text-muted small fst-italic">-</span>}</td>
                                            <td>{member.gender || <span className="text-muted small fst-italic">-</span>}</td>
                                            <td className="pe-3">
                                                {member.city ? (
                                                    <span className="font-size-12">{member.city}/{member.state}</span>
                                                ) : <span className="text-muted small fst-italic">-</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {previewData.length > 100 && (
                            <div className="text-center text-muted small mt-3 fw-medium">
                                Exibindo apenas os 100 primeiros registros por questões de desempenho de tela.
                            </div>
                        )}
                    </div>
                )}

                {isImporting && (
                    <div className="text-center py-5">
                        <ContentLoader />
                        <h5 className="mt-4 mb-2 fw-bold text-dark" style={{ fontFamily: "Outfit, sans-serif" }}>Inserindo registros no Banco de Dados...</h5>
                        <p className="text-muted mb-4 font-size-13">Por favor, não feche ou recarregue esta página.</p>

                        <div style={{ maxWidth: "400px", margin: "0 auto" }}>
                            <Progress 
                                value={progress} 
                                color="success" 
                                className="mb-2" 
                                style={{ height: "10px", borderRadius: "10px", backgroundColor: "rgba(46, 125, 50, 0.08)" }} 
                            />
                            <div className="d-flex justify-content-between text-muted font-size-12 fw-bold" style={{ fontFamily: "Outfit, sans-serif" }}>
                                <span>{progress}%</span>
                                <span>{Math.round((progress / 100) * previewData.length)} / {previewData.length} alunos</span>
                            </div>
                        </div>
                    </div>
                )}
            </Card>
        </React.Fragment>
    );

};

export default ImportMembersPage;
